import React, { Component } from 'react';
import PropTypes from 'prop-types';

export default class SuggestInput extends Component {
  static propTypes = {
    uuid: PropTypes.string,
    // Expects an array in the form:
    // [ { url: ..., parameter: 'model[method][]' }, {...} ]
    dataOptions: PropTypes.array,
    initialValue: PropTypes.array,
    multiValue: PropTypes.bool,
    onRemoveResult: PropTypes.func,
    onPickSuggestion: PropTypes.func,
    placeholder: PropTypes.string,
    inputClass: PropTypes.string
  }

  static defaultProps = {
    onRemoveResult: () => {},
    onPickSuggestion: () => {},
    placeholder: 'Start typing to see suggestions...',
    inputClass: ''
  }

  constructor(props) {
    super(props);

    this.state = {
      inputValue: '',
      value: this.props.initialValue.map(
        v => (
          {
            display: v.name,
            id: v.id,
            type: v.object_type,
            parameter: v.parameter,
          }
        )
      ),
      suggestions: [],
      selectedSuggestionIndex: 0,
    };
  }

  highlight(query, str) {
    const fullStr = str;
    const highlightIndex = fullStr.toLowerCase().indexOf(query);
    let beforeHighlight = fullStr;
    let it = '';
    let afterHighlight = '';
    if (highlightIndex !== -1) {
      beforeHighlight = fullStr.slice(0, highlightIndex);
      it = fullStr.slice(highlightIndex, highlightIndex + query.length);
      afterHighlight = fullStr.slice(highlightIndex + query.length);
    }

    if (it.length !== 0) {
      it = (
        <strong>
          {it}
        </strong>
      );
    }

    return (
      <span>
        {beforeHighlight}
        {it}
        {afterHighlight}
      </span>
    );
  }

  loadSuggestions(query) {
    const promises = this.props.dataOptions.map(option =>
      new Promise(resolve => {
        $.ajax({
          url: option.url,
          type: 'GET',
          data: {
            query,
          },
          success: (data) => {
            const suggestions = data.map(
              datum => ({
                display: datum.name,
                type: datum.object_type,
                highlitDisplay: this.highlight(query, datum.name),
                parameter: option.parameter,
                id: datum.id,
              })
            );
            resolve(suggestions);
          },
        });
      })
    );

    Promise.all(promises).then(allSuggestions => {
      const suggestions = Array.prototype.concat.apply([], allSuggestions);
      // initialize selected suggestion
      if (suggestions.length > 0) {
        suggestions[0].selected = true;
      }
      this.setState({
        suggestions,
        selectedSuggestionIndex: 0,
      });
    });
  }

  removeResult(key) {
    const index = this.state.value.findIndex(element => element.id === key);
    const newValue = this.state.value.slice();
    newValue.splice(index, 1);
    this.setState({
      value: newValue,
    });
    // Trigger custom callback
    this.props.onRemoveResult({ data: this.state.value[index] });
  }

  handleChange = (e) => {
    const value = e.target.value;
    if (value.length >= 3) {
      // load suggestions
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.loadSuggestions(value);
      }, 150);
      this.setState({
        inputValue: value,
      });
    } else {
      this.setState({
        selectedSuggestionIndex: 0,
        suggestions: [],
        inputValue: value,
      });
    }
  }

  hover(index) {
    this.setState({
      selectedSuggestionIndex: index,
    });
  }

  pickSuggestion(index) {
    document.dispatchEvent(new CustomEvent('suggestionSelected', { detail: { data: this.state.suggestions[index], uuid: this.props.uuid } }));
    // shallow copy array;
    const newValue = this.state.value.slice();
    // get new value from selected suggestion;
    newValue.push(this.state.suggestions[index]);
    this.setState({
      inputValue: '',
      value: newValue,
      suggestions: [],
      selectedSuggestionIndex: 0,
    });
    // Trigger custom callback
    this.props.onPickSuggestion({ data: this.state.suggestions[index], uuid: this.props.uuid });
  }

  handleBlur = () => {
    setTimeout(() => {
      this.setState({
        inputValue: '',
        suggestions: [],
        selectedSuggestionIndex: 0,
      });
    }, 200);
  }

  handleKeyDown = (e) => {
    switch (e.which) {
      case 40: // Down Arrow
        if (this.state.suggestions.length > 0) {
          e.preventDefault();
          e.stopPropagation();

          this.setState({
            selectedSuggestionIndex: Math.min(this.state.selectedSuggestionIndex + 1, this.state.suggestions.length - 1),
          });
        }
        break;
      case 38: // Up Arrow
        if (this.state.suggestions.length > 0) {
          e.preventDefault();
          e.stopPropagation();

          this.setState({
            selectedSuggestionIndex: Math.max(this.state.selectedSuggestionIndex - 1, 0),
          });
        }
        break;
      case 13: // Enter key
      case 9: // tab key
        if (this.state.suggestions.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          this.pickSuggestion(this.state.selectedSuggestionIndex);
        }
        break;
      case 27: // Esc key
        this.handleBlur();
        break;
      default:
        break;
    }
  }

  render() {
    let emptySuggestions = '';

    if (this.state.suggestions.length === 0 && this.state.inputValue.length > 3) {
      const notice = "Can't find any matching results.";
      emptySuggestions = (
        <li>
          {notice}
        </li>
      );
    }

    let textField = '';
    if (this.state.value.length === 0 || this.props.multiValue === true) {
      textField = (<input
        type="text"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        placeholder={this.props.placeholder}
        value={this.state.inputValue}
        onChange={this.handleChange}
        onKeyDown={this.handleKeyDown}
        onBlur={this.handleBlur}
        className={this.props.inputClass}
      />);
    }

    return (
      <div className="SuggestInput">
        {textField}
        <div className="SuggestInput-wrapper u-mt-2">
          <ul>
            {
              this.state.suggestions.map(
                (suggestion, i) => {
                  const highlighted = this.state.selectedSuggestionIndex === i ? 'u-bg-old-highlight-color' : '';
                  const suggestionIcon = `Icon-${suggestion.type} Icon`;
                  return (
                    <li
                      key={i}
                      className={highlighted}
                      onMouseOver={() => this.hover(i)}
                      onClick={() => this.pickSuggestion(i)}
                    >
                      <i className={suggestionIcon} />
                      {suggestion.highlitDisplay}
                    </li>
                  );
                }
              )
            }
            {emptySuggestions}
          </ul>
          <div className="SuggestInput-results u-mb-2">
            {
              this.state.value.map(
                value => {
                  return (
                    <span key={value.id}>
                      {value.display}
                      <span
                        className="SuggestInput-results-close"
                        onClick={
                          () => {
                            this.removeResult(value.id);
                          }
                        }
                      >
                        &times;
                      </span>
                      <input name={value.parameter} type="hidden" value={value.id} />
                    </span>
                  );
                }
              )
            }
          </div>
        </div>
        {this.props.dataOptions.map((option, i) => (<input name={option.parameter} type="hidden" key={i} />))}
      </div>
    );
  }
}
